import _ from 'lodash';
import { getFieldDefinitions } from './Definitions';
import Types from './Types';

// Helpers

function loadValue(value, options) {
  if (options.mapToDefault(value)) return options.default();
  return options.load(value);
}

function dumpValue(value, options) {
  if (options.mapToDefault(value)) return options.default();
  return options.dump(value);
}

function hasErrors(errors) {
  const invalid = Object.keys(errors).some((key) => {
    const fieldErrors = errors[key];
    if (_.isPlainObject(fieldErrors)) {
      return hasErrors(fieldErrors);
    }
    return fieldErrors.length > 0;
  });
  return invalid || false;
}

// Strips all __typename keys (originating from GraphQL) from the dumped object
const STRIPPED_KEYS = ['__typename'];

function stripKeysRecursively(value) {
  if (_.isArray(value)) {
    return value.slice(0).map(item => stripKeysRecursively(item));
  } else if (_.isPlainObject(value)) {
    const newValue = {};
    Object.keys(value).forEach((key) => {
      if (!_.includes(STRIPPED_KEYS, key)) {
        newValue[key] = stripKeysRecursively(value[key]);
      }
    });
    return newValue;
  }
  return value;
}

// Mixin

function Form(schemaOptions) {
  const definitions = getFieldDefinitions(schemaOptions);
  const fieldNames = Object.keys(definitions);

  return {
    props: {
      values: {
        type: Object,
        default: () => ({}),
      },
      errors: {
        type: Object,
        default: () => ({}),
      },
      disabled: Boolean,
    },
    data() {
      const loadedValues = this.loadValues(this.values);
      const change = {};
      fieldNames.forEach((key) => {
        change[key] = (value) => {
          this.fields.values[key] = value;
        };
      });

      return {
        isSubmitting: false,
        fieldNames,
        definitions,
        validatingFieldNames: [],
        fields: {
          originalValues: _.cloneDeep(loadedValues),
          values: _.cloneDeep(loadedValues),
          errors: _.cloneDeep(this.loadErrors(this.errors)),
          change,
        },
      };
    },
    watch: {
      values: {
        handler(values, prevValues) {
          if (!_.isEqual(values, prevValues)) {
            const loadedValues = this.loadValues(values);
            this.fields.originalValues = _.cloneDeep(loadedValues);
            this.fields.values = _.cloneDeep(loadedValues);
          }
        },
        deep: true,
      },
      errors: {
        handler(errors) {
          this.fields.errors = _.cloneDeep(this.loadErrors(errors));
        },
        deep: true,
      },
    },
    computed: {
      isEditable() {
        return !this.disabled && !this.isValidating;
      },
      isSubmittable() {
        return !this.isSubmitting && this.isEditable && this.isPopulated;
      },
      isValidating() {
        return this.validatingForm > 0 || this.validatingFieldNames.length > 0;
      },
      fieldData() {
        return {
          original: this.dumpValues(this.fields.originalValues),
          changed: this.dumpValues(this.fields.values),
        };
      },
      hasChanged() {
        return !_.isEqual(this.fieldData.original, this.fieldData.changed);
      },
      requiredFieldNames() {
        return this.fieldNames.filter(key => this.definitions[key].required);
      },
      presentFieldNames() {
        return this.fieldNames.filter((key) => {
          const options = this.definitions[key];
          const value = this.fields.values[key];
          return options.isPresent(value);
        });
      },
      isPopulated() {
        return this.requiredFieldNames.every(key =>
          _.includes(this.presentFieldNames, key));
      },
      isValid() {
        return !this.isInvalid;
      },
      isInvalid() {
        return hasErrors(this.fields.errors);
      },
    },
    methods: {
      originalLoadValues(values) {
        const loadedValues = {};
        _.forOwn(definitions, (options, key) => {
          if (options.loadFrom) {
            let value = null;
            if (values) {
              value = loadValue(values[options.loadFrom], options);
            } else {
              value = options.default();
            }
            loadedValues[key] = value;
          }
        });
        return loadedValues;
      },
      originalDumpValues(values) {
        const dumpedValues = {};
        if (!values) return dumpedValues;
        _.forOwn(definitions, (options, key) => {
          if (options.dumpTo) {
            dumpedValues[options.dumpTo] = dumpValue(values[key], options);
          }
        });
        return stripKeysRecursively(dumpedValues);
      },
      originalLoadErrors(errors) {
        const loadedErrors = {};
        if (!errors) return loadedErrors;
        _.forOwn(definitions, (options, key) => {
          if (options.loadErrorsFrom) {
            const fieldErrors = errors[options.loadErrorsFrom];
            loadedErrors[key] = options.loadErrors(fieldErrors);
          } else {
            loadedErrors[key] = [];
          }
        });
        return loadedErrors;
      },
      // The following load methods are aliases, so we can override them in
      // components but still be able to call the original implementation.
      loadValues(values) {
        return this.originalLoadValues(values);
      },
      dumpValues(values) {
        return this.originalDumpValues(values);
      },
      loadErrors(errors) {
        return this.originalLoadErrors(errors);
      },
      reset() {
        this.fields.values = _.cloneDeep(this.fields.originalValues);
        this.$emit('reset');
      },
      clearErrors() {
        _.forOwn(definitions, (options, key) => {
          this.fields.errors[key] = options.loadErrors([]);
        });
      },
      validateForm() {
        return true;
      },
      async validateField(key) {
        const { validators } = this.definitions[key];
        if (!validators.length) return true;

        // Throw an error if the field is currently already validating
        if (_.includes(this.validatingFieldNames, key)) {
          throw new Error(`The field is already validating: ${key}`);
        }

        // Emit an event which indicates that validation started for the field
        this.$emit('validate-field', { key });

        // Enforces #isValidating to be true for the form
        this.validatingFieldNames.push(key);

        // Collect all field validators and wait for them to finish validation
        const { values } = this.fields;
        const value = values[key];
        const validations = validators.map(validator =>
          validator.call(this, value, key, values));
        const results = await Promise.all(validations);

        // Collect the error messages from the promise results
        const messages = [];
        results.forEach(({ isValid, message }) => {
          if (!isValid) messages.push(message);
        });
        const isValid = !messages.length;
        this.fields.errors[key] = messages;

        // Remove field from #validatingFieldNames
        this.validatingFieldNames = _.without(this.validatingFieldNames, key);

        // Emit and return validation result for the particular field
        this.$emit('validated-field', { key, isValid, messages });
        return isValid;
      },
      async validate() {
        if (this.isValidating) {
          throw new Error('The form is already validating');
        }
        this.$emit('validate');
        this.validatingForm += 1;
        this.clearErrors();
        await Promise.all([
          this.validateForm(),
          ...this.fieldNames.map(key => this.validateField(key)),
        ]);
        this.validatingForm -= 1;
        this.$emit('validated', {
          isValid: this.isValid,
          messages: this.fields.errors,
        });
        return this.isValid;
      },
      async submit() {
        if (!this.isSubmittable) return false;
        this.isSubmitting = true;
        try {
          const isValid = await this.validate();
          if (isValid) {
            const { changed, original } = this.fieldData;
            this.$emit('submit', _.cloneDeep(changed), original);
          }
          this.isSubmitting = false;
          return isValid;
        } catch (error) {
          this.isSubmitting = false;
          throw error;
        }
      },
    },
  };
}

Object.assign(Form, { Types });

export default Form;



// WEBPACK FOOTER //
// ./mixins/Form/index.js